import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";

export default function ContactSection() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-12 md:py-16 px-4 sm:px-6 md:px-12"
      data-testid="contact-section"
    >
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p
            className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 font-semibold"
            style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
          >
            Get in Touch
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-3"
            style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
          >
            Contact
          </h2>
          <p
            className="text-sm sm:text-base font-light mb-6 sm:mb-8"
            style={{ color: "#999999", fontFamily: "'Manrope', sans-serif" }}
          >
            Please reach out through parents for further communication
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4"
        >
          {/* Father */}
          <div
            className="p-4 sm:p-5 rounded-xl transition-all duration-300 hover:shadow-xs"
            style={{ background: "#F7F4EF", border: "1px solid #E6E2DD" }}
          >
            <p className="text-xs tracking-[0.15em] uppercase font-semibold mb-1.5" style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}>
              Father
            </p>
            <p className="text-base sm:text-lg font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}>
              Ramnath Dangui
            </p>
            <div className="flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
              <a
                href="tel:9423323955"
                className="text-sm sm:text-base font-normal hover:underline"
                style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif" }}
                data-testid="father-phone"
              >
                94233 23955
              </a>
            </div>
          </div>

          {/* Mother */}
          <div
            className="p-4 sm:p-5 rounded-xl transition-all duration-300 hover:shadow-xs"
            style={{ background: "#F7F4EF", border: "1px solid #E6E2DD" }}
          >
            <p className="text-xs tracking-[0.15em] uppercase font-semibold mb-1.5" style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}>
              Mother
            </p>
            <p className="text-base sm:text-lg font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}>
              Ujwala Dangui
            </p>
            <div className="flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
              <a
                href="tel:9403817309"
                className="text-sm sm:text-base font-normal hover:underline"
                style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif" }}
                data-testid="mother-phone"
              >
                94038 17309
              </a>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-center justify-center gap-2 pt-2">
            <Mail className="w-4 h-4" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
            <a
              href="mailto:nishaddangui3@gmail.com"
              className="text-sm sm:text-base font-normal hover:underline"
              style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif" }}
              data-testid="email-link"
            >
              nishaddangui3@gmail.com
            </a>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-10 sm:mt-12 pt-6 sm:pt-8"
          style={{ borderTop: "1px solid #E6E2DD" }}
        >
          <p
            className="text-xs sm:text-sm italic font-light"
            style={{ color: "#C4B298", fontFamily: "'Playfair Display', serif" }}
          >
            Shared with warmth and sincerity.
          </p>
          <p
            className="text-[11px] sm:text-xs mt-3 tracking-wide"
            style={{ color: "#D4D0CB", fontFamily: "'Manrope', sans-serif" }}
          >
            This profile is private and confidential. Please do not share.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
